import React, { useRef, useEffect, useCallback } from 'react'
import { GeoapifyContext, GeoapifyGeocoderAutocomplete } from '@geoapify/react-geocoder-autocomplete'
import '@geoapify/geocoder-autocomplete/styles/minimal.css'

type GeoapifyGeocoderAutocompleteProps = {
  values: any
  placeholder: string
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  useFixedPosition?: boolean 
}

const GeoapifyAutocompleteComponent: React.FC<GeoapifyGeocoderAutocompleteProps> = ({
  values,
  handleChange,
  placeholder,
  onBlur,
  useFixedPosition = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const blurHandlerRef = useRef<((e: FocusEvent) => void) | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)

  const findInput = useCallback((): HTMLInputElement | null => {
    if (!containerRef.current) return null
    return (
      containerRef.current.querySelector('input') ||
      containerRef.current.querySelector('input[type="text"]') ||
      containerRef.current.querySelector('input[placeholder]') ||
      null
    )
  }, [])

  useEffect(() => {
    if (!onBlur || !containerRef.current) return

    const attachBlurHandler = () => {
      const input = findInput()
      if (input && !blurHandlerRef.current) {
        const handleBlur = (e: FocusEvent) => {
          if (onBlur) {
            const syntheticEvent = {
              ...e,
              target: e.target as HTMLInputElement,
              currentTarget: e.currentTarget as HTMLInputElement,
              preventDefault: () => e.preventDefault(),
              stopPropagation: () => e.stopPropagation(),
            } as unknown as React.FocusEvent<HTMLInputElement>
            onBlur(syntheticEvent)
          }
        }

        blurHandlerRef.current = handleBlur
        input.addEventListener('blur', handleBlur, true)
        return true
      }
      return false
    }
    attachBlurHandler()

    observerRef.current = new MutationObserver(() => {
      const input = findInput()
      if (input && !blurHandlerRef.current) {
        attachBlurHandler()
      }
    })

    observerRef.current.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: false,
    })
    const timeoutId = setTimeout(() => {
      attachBlurHandler()
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (blurHandlerRef.current) {
        const input = findInput()
        if (input) {
          input.removeEventListener('blur', blurHandlerRef.current, true)
        }
        blurHandlerRef.current = null
      }
    }
  }, [onBlur, findInput])

  useEffect(() => {
    if (!onBlur || !containerRef.current) return

    const handleFocusOut = (e: FocusEvent) => {
      if (containerRef.current && e.target instanceof Node) {
        const input = findInput()
        if (input && !containerRef.current.contains(e.relatedTarget as Node)) {
          const syntheticEvent = {
            ...e,
            target: input,
            currentTarget: input,
            preventDefault: () => e.preventDefault(),
            stopPropagation: () => e.stopPropagation(),
          } as unknown as React.FocusEvent<HTMLInputElement>
          onBlur(syntheticEvent)
        }
      }
    }

    containerRef.current.addEventListener('focusout', handleFocusOut, true)

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('focusout', handleFocusOut, true)
      }
    }
  }, [onBlur, findInput])

  // Fix dropdown positioning using fixed property to not consume dialog space
  // Only applies when useFixedPosition prop is true
  useEffect(() => {
    if (!useFixedPosition || !containerRef.current) return

    const updateDropdownPosition = () => {
      const input = findInput()
      if (!input) return

      // Find the dropdown items container
      const dropdown = containerRef.current?.querySelector(
        '.geoapify-autocomplete-items'
      ) as HTMLElement

      if (dropdown) {
        // Get input field position relative to viewport
        const inputRect = input.getBoundingClientRect()
        
        // Calculate available space below the input
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - inputRect.bottom - 4 // 4px margin
        const minDropdownHeight = 100 // Minimum height for dropdown
        const maxDropdownHeight = 300 // Maximum preferred height
        const availableHeight = Math.max(minDropdownHeight, Math.min(maxDropdownHeight, spaceBelow - 20)) // 20px padding from bottom
        
        // Set fixed positioning below the input field
        // Fixed positioning is relative to viewport, so we don't add scroll values
        dropdown.style.position = 'fixed'
        dropdown.style.top = `${inputRect.bottom + 4}px` // 4px margin below input
        dropdown.style.left = `${inputRect.left}px`
        dropdown.style.width = `${inputRect.width}px`
        dropdown.style.maxHeight = `${availableHeight}px`
        dropdown.style.overflowY = 'auto'
        dropdown.style.overflowX = 'hidden'
        dropdown.style.zIndex = '1055'
        dropdown.style.backgroundColor = '#ffffff'
        dropdown.style.border = '1px solid #e4e6ef'
        dropdown.style.borderRadius = '0.475rem'
        dropdown.style.boxShadow = '0px 4px 16px rgba(0, 0, 0, 0.1)'
        dropdown.style.marginTop = '0'
        dropdown.style.marginBottom = '0'
        dropdown.style.paddingBottom = '0'
        
        // Ensure scrollbar is visible
        dropdown.style.scrollBehavior = 'smooth'
      }
    }

    // Watch for dropdown appearance and update position
    const observer = new MutationObserver(() => {
      updateDropdownPosition()
    })

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    // Update position on scroll and resize
    const handleScroll = () => updateDropdownPosition()
    const handleResize = () => updateDropdownPosition()

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    // Also watch the modal for scroll events
    const modal = containerRef.current.closest('.modal, .custom-reschedule-dialog')
    if (modal) {
      modal.addEventListener('scroll', handleScroll, true)
    }

    // Initial update
    updateDropdownPosition()

    // Periodic update to catch late renders
    const intervalId = setInterval(updateDropdownPosition, 100)

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
      if (modal) {
        modal.removeEventListener('scroll', handleScroll, true)
      }
      clearInterval(intervalId)
    }
  }, [useFixedPosition, findInput])

  return (
    <GeoapifyContext apiKey={process.env.REACT_APP_GEOCODER_AUTOCOMPLETE_KEY}>
      <div ref={containerRef}>
        <GeoapifyGeocoderAutocomplete
          placeSelect={handleChange}
          placeholder={placeholder}
          value={values}
        />
      </div>
    </GeoapifyContext>
  )
}

export default GeoapifyAutocompleteComponent
